import cloneDeep from 'lodash/cloneDeep';
import { isContentType } from '@core/types/typeGuards';
import { Asset, Fixture, Profile } from '@graphql/__generated__/graphql';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { CONTENT_TYPES } from '@core/constants/contentTypes.constants';
import { ContentfulImage, ContentfulLogo } from '@stadionhq-v3/contentful-ui';
import { Id, MATCH_EVENT_TYPES } from '@stadionhq-v3/core';
import {
  LineUpPlayerCardProps,
  LineUpsProps,
  PlayerSilhouette,
} from '@stadionhq-v3/football-ui';
import {
  CardRed,
  CardSecondYellow,
  CardYellow,
  Goal,
  OwnGoal,
  Substitution,
} from '@components/icons';
import { AblyMutator } from '@stadionhq-v3/ably';
import { Translations } from '@lib/dictionary-keys/__generated__';
import { orderByPosition, orderByShirtNumber } from '@utils/lineUps';
import { assetTransform } from '../atoms';

export type LineUpsTransFormTranslations = {
  formation: Translations['matchCentre']['lineupsLabel']['formation'];
  manager: Translations['matchCentre']['lineupsLabel']['manager'];
  startingXI: Translations['matchCentre']['lineupsLabel']['startingXI'];
  substitutions: Translations['matchCentre']['lineupsLabel']['substitutions'];
};

export type Headshots = (CleanContentful<Profile> | undefined)[] | undefined;

export type SingleLineUpLiveProps = Pick<
  LineUpsProps,
  'id' | 'teamName' | 'formation' | 'managerName' | 'starting' | 'subs'
>;

export type LineUpsLiveProps = {
  home: SingleLineUpLiveProps;
  away: SingleLineUpLiveProps;
};

export type SingleLineUpsStaticProps = Pick<
  LineUpsProps,
  'crest' | 'labels' | 'icons'
>;

export type LineUpsStaticProps = {
  home: SingleLineUpsStaticProps;
  away: SingleLineUpsStaticProps;
};

const clientTeamWithHeadshots = (
  lineup: LineUpPlayerCardProps[],
  headshots: Headshots
) =>
  lineup
    ? lineup.map((player) => {
        const matchingHeadshot = headshots?.find(
          (headshot) => headshot?.dataProviderId === player.id
        );

        return {
          ...player,
          profileImage:
            matchingHeadshot &&
            isContentType(
              matchingHeadshot.headshot,
              CONTENT_TYPES.IMAGE_TAG
            ) ? (
              <ContentfulImage
                {...matchingHeadshot.headshot}
                breakpoints={['s']}
                customImageSizes={{
                  360: 250,
                }}
              />
            ) : (
              <PlayerSilhouette />
            ),
        };
      })
    : [];

export const lineUpsAblyMutator = (
  headShots: Headshots,
  {
    teamId,
    homeTeamId,
    awayTeamId,
  }: {
    teamId: string | undefined;
    homeTeamId: string | undefined;
    awayTeamId: string | undefined;
  }
): AblyMutator<LineUpsLiveProps, { lineUps: LineUpsLiveProps }> => {
  const clientIsHomeTeam = homeTeamId === teamId;
  const clientIsAwayTeam = awayTeamId === teamId;

  return ({ state, response }) => {
    const data = response ? cloneDeep(response?.lineUps) : cloneDeep(state);

    const { home, away } = data;

    return {
      home: {
        ...home,
        formation: '',
        starting:
          home?.starting && clientIsHomeTeam
            ? clientTeamWithHeadshots(
                orderByPosition(home?.starting),
                headShots as CleanContentful<Profile>[]
              )
            : orderByPosition(home?.starting),
        subs:
          home?.subs && clientIsHomeTeam
            ? clientTeamWithHeadshots(
                orderByShirtNumber(home?.subs),
                headShots as CleanContentful<Profile>[]
              )
            : orderByShirtNumber(home?.subs),
      },
      away: {
        ...away,
        formation: '',
        starting:
          away?.starting && clientIsAwayTeam
            ? clientTeamWithHeadshots(
                orderByPosition(away?.starting),
                headShots as CleanContentful<Profile>[]
              )
            : orderByPosition(away?.starting),
        subs:
          away?.subs && clientIsAwayTeam
            ? clientTeamWithHeadshots(
                orderByShirtNumber(away?.subs),
                headShots as CleanContentful<Profile>[]
              )
            : orderByShirtNumber(away?.subs),
      },
    };
  };
};

export const lineUpsTransform = (
  fixture: (CleanContentful<Fixture & Id> | undefined) | undefined,
  labels: LineUpsTransFormTranslations,
  headShots: Headshots,
  fallbackClubCrest?: CleanContentful<Asset>
) => {
  const clientIsHomeTeam =
    fixture?.homeClub?.dataProviderId === fixture?.team?.dataProviderId;
  const clientIsAwayTeam =
    fixture?.awayClub?.dataProviderId === fixture?.team?.dataProviderId;

  const getClubCrest = (
    club:
      | CleanContentful<Fixture>['homeClub']
      | CleanContentful<Fixture>['awayClub']
  ) =>
    club?.clubCrest ? (
      <ContentfulLogo {...club?.clubCrest} width={56} height={56} />
    ) : fallbackClubCrest ? (
      <ContentfulLogo
        {...assetTransform(fallbackClubCrest)}
        width={56}
        height={56}
      />
    ) : undefined;

  const homeLineUp: SingleLineUpsStaticProps = {
    crest: getClubCrest(fixture?.homeClub),
    labels,
    icons: {
      [MATCH_EVENT_TYPES.GOAL]: <Goal />,
      [MATCH_EVENT_TYPES.OWN_GOAL]: <OwnGoal />,
      [MATCH_EVENT_TYPES.RED_CARD]: <CardRed />,
      [MATCH_EVENT_TYPES.SECOND_YELLOW]: <CardSecondYellow />,
      [MATCH_EVENT_TYPES.SUB_OFF]: <Substitution />,
      [MATCH_EVENT_TYPES.SUB_ON]: <Substitution />,
      [MATCH_EVENT_TYPES.YELLOW_CARD]: <CardYellow />,
      [MATCH_EVENT_TYPES.PENALTY_GOAL]: <Goal />,
    },
  };

  const homeLineUpLiveProps: SingleLineUpLiveProps = {
    id: fixture?.matchData?.lineUps?.home?.id,
    teamName: fixture?.matchData?.lineUps?.home?.name,
    formation: '',
    managerName: fixture?.matchData?.lineUps?.home?.managerName,
    starting:
      fixture?.matchData?.lineUps?.home?.starting && clientIsHomeTeam
        ? clientTeamWithHeadshots(
            orderByPosition(fixture?.matchData?.lineUps?.home?.starting),
            headShots as CleanContentful<Profile>[]
          )
        : orderByPosition(fixture?.matchData?.lineUps?.home?.starting),
    subs:
      fixture?.matchData?.lineUps?.home?.subs && clientIsHomeTeam
        ? clientTeamWithHeadshots(
            orderByShirtNumber(fixture?.matchData?.lineUps?.home?.subs),
            headShots as CleanContentful<Profile>[]
          )
        : orderByShirtNumber(fixture?.matchData?.lineUps?.home?.subs),
  };

  const awayLineUp: SingleLineUpsStaticProps = {
    crest: getClubCrest(fixture?.awayClub),
    labels,
    icons: {
      [MATCH_EVENT_TYPES.GOAL]: <Goal />,
      [MATCH_EVENT_TYPES.OWN_GOAL]: <OwnGoal />,
      [MATCH_EVENT_TYPES.RED_CARD]: <CardRed />,
      [MATCH_EVENT_TYPES.SECOND_YELLOW]: <CardSecondYellow />,
      [MATCH_EVENT_TYPES.SUB_OFF]: <Substitution />,
      [MATCH_EVENT_TYPES.SUB_ON]: <Substitution />,
      [MATCH_EVENT_TYPES.YELLOW_CARD]: <CardYellow />,
      [MATCH_EVENT_TYPES.PENALTY_GOAL]: <Goal />,
    },
  };

  const awayLineUpLiveProps: SingleLineUpLiveProps = {
    id: fixture?.matchData?.lineUps?.away?.id,
    teamName: fixture?.matchData?.lineUps?.away?.name,
    formation: '',
    managerName: fixture?.matchData?.lineUps?.away?.managerName,
    starting:
      fixture?.matchData?.lineUps?.away?.starting && clientIsAwayTeam
        ? clientTeamWithHeadshots(
            orderByPosition(fixture?.matchData?.lineUps?.away?.starting),
            headShots as CleanContentful<Profile>[]
          )
        : orderByPosition(fixture?.matchData?.lineUps?.away?.starting),
    subs:
      fixture?.matchData?.lineUps?.away?.subs && clientIsAwayTeam
        ? clientTeamWithHeadshots(
            orderByShirtNumber(fixture?.matchData?.lineUps?.away?.subs),
            headShots as CleanContentful<Profile>[]
          )
        : orderByShirtNumber(fixture?.matchData?.lineUps?.away?.subs),
  };

  return {
    homeLineUpLiveProps,
    homeLineUp,
    awayLineUpLiveProps,
    awayLineUp,
  };
};
